import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { User, Building2, Phone, Mail, ArrowLeft, Save, Shield, X, Lock } from 'lucide-react'
import { useAuth } from '../hooks/useAuth'
import { useToast } from '../hooks/useToast'
import { supabase } from '../lib/supabase'

type PhoneStep = 'DISPLAY' | 'EDITING' | 'CODE_SENT'

export const ProfilePage: React.FC = () => {
  const { user, getAccessToken, checkAuthMethods, resetPassword } = useAuth()
  const navigate = useNavigate()
  const { showToast } = useToast()

  const [fullName, setFullName] = useState('')
  const [company, setCompany] = useState('')
  const [verifiedPhone, setVerifiedPhone] = useState('')
  const [initialValues, setInitialValues] = useState({ fullName: '', company: '' })

  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  // Phone verification state
  const [phoneStep, setPhoneStep] = useState<PhoneStep>('DISPLAY')
  const [newPhone, setNewPhone] = useState('')
  const [verifyCode, setVerifyCode] = useState('')
  const [phoneSending, setPhoneSending] = useState(false)
  const [phoneVerifying, setPhoneVerifying] = useState(false)
  const [phoneError, setPhoneError] = useState('')
  const [resendCooldown, setResendCooldown] = useState(0)
  const [removingPhone, setRemovingPhone] = useState(false)

  // Password change state
  const [hasPassword, setHasPassword] = useState(true)
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [changingPassword, setChangingPassword] = useState(false)
  const [passwordError, setPasswordError] = useState('')
  const [sendingSetupEmail, setSendingSetupEmail] = useState(false)
  const [setupEmailSent, setSetupEmailSent] = useState(false)

  useEffect(() => {
    const fetchProfile = async () => {
      if (!user) return

      const { data, error: fetchError } = await supabase
        .from('user_profiles')
        .select('full_name, company, phone_number')
        .eq('id', user.id)
        .single()

      if (fetchError) {
        console.error('Error fetching profile:', fetchError)
        showToast('error', 'Failed to load profile')
      } else if (data) {
        setFullName(data.full_name || '')
        setCompany(data.company || '')
        setVerifiedPhone(data.phone_number || '')
        setInitialValues({
          fullName: data.full_name || '',
          company: data.company || ''
        })
      }

      // Check if user has a password set
      if (user.email) {
        try {
          const authInfo = await checkAuthMethods(user.email)
          setHasPassword(authInfo.hasPassword)
        } catch {
          // Default to showing "Change Password" if check fails
          setHasPassword(true)
        }
      }

      setLoading(false)
    }

    fetchProfile()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user])

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown <= 0) return
    const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000)
    return () => clearTimeout(timer)
  }, [resendCooldown])

  const hasChanges =
    fullName !== initialValues.fullName ||
    company !== initialValues.company

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault()
    setSaving(true)

    const { error: updateError } = await supabase
      .from('user_profiles')
      .update({
        full_name: fullName,
        company: company
      })
      .eq('id', user!.id)

    if (updateError) {
      showToast('error', updateError.message || 'Failed to update profile')
      setSaving(false)
      return
    }

    setInitialValues({ fullName, company })
    setSaving(false)
    showToast('success', 'Profile updated successfully')
  }

  const handleSendVerification = async () => {
    setPhoneError('')
    const trimmed = newPhone.trim()

    if (!/^\+44\d{10}$/.test(trimmed)) {
      setPhoneError('Please enter a valid UK phone number (e.g., +447911123456)')
      return
    }

    setPhoneSending(true)
    try {
      const token = await getAccessToken()
      const response = await fetch('/.netlify/functions/send-phone-verification', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ phone: trimmed })
      })
      const data = await response.json()
      if (!response.ok) throw new Error(data.error || 'Failed to send verification code')

      setPhoneStep('CODE_SENT')
      setResendCooldown(60)
    } catch (err: any) {
      setPhoneError(err.message || 'Failed to send code')
    }
    setPhoneSending(false)
  }

  const handleVerifyCode = async () => {
    setPhoneError('')

    if (verifyCode.length !== 6) {
      setPhoneError('Please enter the 6-digit code')
      return
    }

    setPhoneVerifying(true)
    try {
      const token = await getAccessToken()
      const response = await fetch('/.netlify/functions/verify-phone', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ code: verifyCode })
      })
      const data = await response.json()
      if (!response.ok) throw new Error(data.error || 'Verification failed')

      // Phone verified and saved by the backend
      setVerifiedPhone(data.phone)
      setPhoneStep('DISPLAY')
      setNewPhone('')
      setVerifyCode('')
      showToast('success', 'Phone number verified successfully')
    } catch (err: any) {
      setPhoneError(err.message || 'Invalid code')
    }
    setPhoneVerifying(false)
  }

  const handleRemovePhone = async () => {
    setPhoneError('')
    setRemovingPhone(true)

    const { error: updateError } = await supabase
      .from('user_profiles')
      .update({ phone_number: null })
      .eq('id', user!.id)

    if (updateError) {
      setPhoneError(updateError.message || 'Failed to remove phone number')
      setRemovingPhone(false)
      return
    }

    setVerifiedPhone('')
    showToast('success', 'Phone number removed')
    setRemovingPhone(false)
  }

  const cancelPhoneEdit = () => {
    setPhoneStep('DISPLAY')
    setNewPhone('')
    setVerifyCode('')
    setPhoneError('')
    setResendCooldown(0)
  }

  const handleChangePassword = async () => {
    setPasswordError('')

    if (!currentPassword) {
      setPasswordError('Please enter your current password')
      return
    }

    if (newPassword.length < 6) {
      setPasswordError('New password must be at least 6 characters')
      return
    }

    if (newPassword !== confirmPassword) {
      setPasswordError('Passwords do not match')
      return
    }

    setChangingPassword(true)
    try {
      // Verify current password server-side
      const token = await getAccessToken()
      const verifyRes = await fetch('/.netlify/functions/verify-current-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ currentPassword })
      })
      const verifyData = await verifyRes.json()

      if (!verifyRes.ok) {
        setPasswordError(verifyData.error || 'Failed to verify password')
        setChangingPassword(false)
        return
      }

      if (!verifyData.valid) {
        setPasswordError('Current password is incorrect')
        setChangingPassword(false)
        return
      }

      // Current password verified — update to new password
      const { error: pwError } = await supabase.auth.updateUser({ password: newPassword })

      if (pwError) {
        setPasswordError(pwError.message)
        setChangingPassword(false)
        return
      }

      setCurrentPassword('')
      setNewPassword('')
      setConfirmPassword('')
      setChangingPassword(false)
      showToast('success', 'Password updated successfully')
    } catch (err: any) {
      setPasswordError(err.message || 'Something went wrong')
      setChangingPassword(false)
    }
  }

  const handleSendSetupEmail = async () => {
    setPasswordError('')
    setSendingSetupEmail(true)
    try {
      const result = await resetPassword(user!.email!)
      if (result.error) {
        setPasswordError(result.error.message)
      } else {
        setSetupEmailSent(true)
      }
    } catch (err: any) {
      setPasswordError(err.message || 'Failed to send setup email')
    }
    setSendingSetupEmail(false)
  }

  const labelStyle: React.CSSProperties = {
    display: 'block',
    fontSize: '14px',
    fontWeight: '600',
    color: '#374151',
    marginBottom: '12px',
    fontFamily: 'Lexend Deca, sans-serif'
  }

  const inputStyle: React.CSSProperties = {
    width: '100%',
    paddingLeft: '48px',
    paddingRight: '16px',
    paddingTop: '16px',
    paddingBottom: '16px',
    border: '1px solid #d1d5db',
    borderRadius: '12px',
    fontSize: '16px',
    background: 'white',
    color: '#111827',
    fontFamily: 'Lexend Deca, sans-serif',
    outline: 'none',
    transition: 'all 0.2s',
    boxSizing: 'border-box' as const
  }

  const iconStyle: React.CSSProperties = {
    position: 'absolute',
    left: '16px',
    top: '50%',
    transform: 'translateY(-50%)',
    height: '20px',
    width: '20px',
    color: '#9ca3af'
  }

  const handleInputFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    e.target.style.borderColor = '#3b82f6'
    e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)'
  }

  const handleInputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    e.target.style.borderColor = '#d1d5db'
    e.target.style.boxShadow = 'none'
  }

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(to bottom right, #f1f5f9, #dbeafe, #e0e7ff)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '64px',
            height: '64px',
            background: 'white',
            borderRadius: '50%',
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
            marginBottom: '16px'
          }}>
            <div style={{
              animation: 'spin 1s linear infinite',
              borderRadius: '50%',
              height: '32px',
              width: '32px',
              border: '2px solid #2563eb',
              borderBottomColor: 'transparent'
            }}></div>
          </div>
          <p style={{
            color: '#6b7280',
            fontWeight: '500',
            fontFamily: 'Lexend Deca, sans-serif'
          }}>
            Loading your profile...
          </p>
        </div>
      </div>
    )
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(to bottom right, #f1f5f9, #dbeafe, #e0e7ff)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '24px'
    }}>
      <div style={{ width: '100%', maxWidth: '600px' }}>
        {/* Card Container */}
        <div style={{
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(8px)',
          borderRadius: '24px',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          overflow: 'hidden'
        }}>
          {/* Header Section */}
          <div style={{
            background: 'linear-gradient(to right, #2563eb, #4f46e5)',
            padding: '32px',
            textAlign: 'center',
            position: 'relative'
          }}>
            <button
              onClick={() => navigate('/')}
              style={{
                position: 'absolute',
                top: '16px',
                left: '16px',
                background: 'rgba(255, 255, 255, 0.2)',
                border: 'none',
                borderRadius: '12px',
                padding: '8px 12px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                color: 'white',
                fontFamily: 'Lexend Deca, sans-serif',
                fontSize: '14px',
                fontWeight: '500',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255, 255, 255, 0.3)'
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255, 255, 255, 0.2)'
              }}
            >
              <ArrowLeft style={{ height: '16px', width: '16px', marginRight: '4px' }} />
              Back
            </button>

            <div style={{
              margin: '0 auto 16px',
              height: '64px',
              width: '64px',
              background: 'rgba(255, 255, 255, 0.2)',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <User style={{ height: '32px', width: '32px', color: 'white' }} />
            </div>
            <h1 style={{
              fontSize: '30px',
              fontWeight: 'bold',
              color: 'white',
              marginBottom: '8px',
              fontFamily: 'Lexend Deca, sans-serif'
            }}>
              Profile
            </h1>
            <p style={{
              color: '#bfdbfe',
              fontWeight: '500',
              fontFamily: 'Lexend Deca, sans-serif'
            }}>
              Manage your account details
            </p>
          </div>

          {/* Form Section */}
          <div style={{ padding: '32px' }}>
            {/* Name & Company Form */}
            <form onSubmit={handleSave} style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              {/* Email Field (read-only) */}
              <div>
                <label htmlFor="email" style={labelStyle}>Email address</label>
                <div style={{ position: 'relative' }}>
                  <input
                    id="email"
                    type="email"
                    value={user?.email || ''}
                    disabled
                    style={{
                      ...inputStyle,
                      border: '1px solid #e5e7eb',
                      background: '#f9fafb',
                      color: '#6b7280',
                      cursor: 'not-allowed'
                    }}
                  />
                  <Mail style={iconStyle} />
                </div>
              </div>

              {/* Full Name Field */}
              <div>
                <label htmlFor="fullName" style={labelStyle}>Full Name</label>
                <div style={{ position: 'relative' }}>
                  <input
                    id="fullName"
                    type="text"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    placeholder="Enter your full name"
                    style={inputStyle}
                    onFocus={handleInputFocus}
                    onBlur={handleInputBlur}
                  />
                  <User style={iconStyle} />
                </div>
              </div>

              {/* Organisation Field */}
              <div>
                <label htmlFor="organisation" style={labelStyle}>Organisation</label>
                <div style={{ position: 'relative' }}>
                  <input
                    id="organisation"
                    type="text"
                    value={company}
                    onChange={(e) => setCompany(e.target.value)}
                    placeholder="Enter your organisation"
                    style={inputStyle}
                    onFocus={handleInputFocus}
                    onBlur={handleInputBlur}
                  />
                  <Building2 style={iconStyle} />
                </div>
              </div>

              {/* Save Button */}
              <button
                type="submit"
                disabled={!hasChanges || saving}
                style={{
                  width: '100%',
                  padding: '16px 24px',
                  background: (!hasChanges || saving)
                    ? '#9ca3af'
                    : 'linear-gradient(to right, #2563eb, #4f46e5)',
                  color: 'white',
                  fontWeight: '600',
                  borderRadius: '12px',
                  boxShadow: (!hasChanges || saving)
                    ? 'none'
                    : '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
                  border: 'none',
                  cursor: (!hasChanges || saving) ? 'not-allowed' : 'pointer',
                  fontSize: '16px',
                  fontFamily: 'Lexend Deca, sans-serif',
                  transition: 'all 0.2s',
                  opacity: (!hasChanges || saving) ? 0.6 : 1
                }}
                onMouseEnter={(e) => {
                  if (hasChanges && !saving) {
                    const target = e.currentTarget as HTMLButtonElement
                    target.style.background = 'linear-gradient(to right, #1d4ed8, #4338ca)'
                    target.style.boxShadow = '0 20px 25px -5px rgba(0, 0, 0, 0.1)'
                  }
                }}
                onMouseLeave={(e) => {
                  if (hasChanges && !saving) {
                    const target = e.currentTarget as HTMLButtonElement
                    target.style.background = 'linear-gradient(to right, #2563eb, #4f46e5)'
                    target.style.boxShadow = '0 10px 15px -3px rgba(0, 0, 0, 0.1)'
                  }
                }}
              >
                {saving ? (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <div style={{
                      animation: 'spin 1s linear infinite',
                      borderRadius: '50%',
                      height: '20px',
                      width: '20px',
                      border: '2px solid white',
                      borderBottomColor: 'transparent',
                      marginRight: '8px'
                    }}></div>
                    Saving...
                  </div>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Save style={{ height: '20px', width: '20px', marginRight: '8px' }} />
                    Save Changes
                  </div>
                )}
              </button>
            </form>

            {/* Divider */}
            <div style={{
              borderTop: '1px solid #e5e7eb',
              marginTop: '32px',
              paddingTop: '32px'
            }}>
              {/* Phone Number Section */}
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
                <Shield style={{ height: '20px', width: '20px', color: '#2563eb', marginRight: '8px' }} />
                <h3 style={{
                  fontSize: '16px',
                  fontWeight: '600',
                  color: '#1f2937',
                  fontFamily: 'Lexend Deca, sans-serif'
                }}>
                  Phone Number (SMS Authentication)
                </h3>
              </div>
              <p style={{
                fontSize: '13px',
                color: '#6b7280',
                fontFamily: 'Lexend Deca, sans-serif',
                marginBottom: '16px'
              }}>
                A verified phone number enables SMS code sign-in as an additional authentication method.
              </p>

              {/* Phone Error Message */}
              {phoneError && (
                <div style={{
                  borderRadius: '12px',
                  padding: '12px 16px',
                  background: '#fef2f2',
                  border: '1px solid #fecaca',
                  marginBottom: '16px'
                }}>
                  <p style={{ fontSize: '14px', fontWeight: '500', color: '#dc2626', fontFamily: 'Lexend Deca, sans-serif' }}>
                    {phoneError}
                  </p>
                </div>
              )}

              {/* DISPLAY state */}
              {phoneStep === 'DISPLAY' && (
                <div>
                  {verifiedPhone ? (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      background: '#f9fafb',
                      borderRadius: '12px',
                      padding: '16px',
                      border: '1px solid #e5e7eb'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <Phone style={{ height: '20px', width: '20px', color: '#16a34a', marginRight: '12px' }} />
                        <div>
                          <p style={{
                            fontSize: '16px',
                            fontWeight: '600',
                            color: '#1f2937',
                            fontFamily: 'Lexend Deca, sans-serif'
                          }}>
                            {verifiedPhone}
                          </p>
                          <p style={{
                            fontSize: '12px',
                            color: '#16a34a',
                            fontFamily: 'Lexend Deca, sans-serif',
                            marginTop: '2px'
                          }}>
                            Verified
                          </p>
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button
                          onClick={() => { setPhoneError(''); setNewPhone(''); setPhoneStep('EDITING') }}
                          style={{
                            background: 'transparent',
                            border: '1px solid #d1d5db',
                            borderRadius: '8px',
                            padding: '8px 12px',
                            fontSize: '13px',
                            fontWeight: '500',
                            color: '#374151',
                            fontFamily: 'Lexend Deca, sans-serif',
                            cursor: 'pointer',
                            transition: 'all 0.2s'
                          }}
                          onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#3b82f6'; e.currentTarget.style.color = '#2563eb' }}
                          onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#d1d5db'; e.currentTarget.style.color = '#374151' }}
                        >
                          Change
                        </button>
                        <button
                          onClick={handleRemovePhone}
                          disabled={removingPhone}
                          style={{
                            background: 'transparent',
                            border: '1px solid #fca5a5',
                            borderRadius: '8px',
                            padding: '8px 12px',
                            fontSize: '13px',
                            fontWeight: '500',
                            color: '#dc2626',
                            fontFamily: 'Lexend Deca, sans-serif',
                            cursor: removingPhone ? 'not-allowed' : 'pointer',
                            transition: 'all 0.2s',
                            opacity: removingPhone ? 0.5 : 1
                          }}
                          onMouseEnter={(e) => { if (!removingPhone) { e.currentTarget.style.background = '#fef2f2' } }}
                          onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent' }}
                        >
                          {removingPhone ? 'Removing...' : 'Remove'}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button
                      onClick={() => { setPhoneError(''); setNewPhone(''); setPhoneStep('EDITING') }}
                      style={{
                        width: '100%',
                        padding: '16px',
                        background: 'white',
                        border: '2px dashed #d1d5db',
                        borderRadius: '12px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                        transition: 'all 0.2s',
                        fontFamily: 'Lexend Deca, sans-serif',
                        fontSize: '14px',
                        fontWeight: '500',
                        color: '#6b7280'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = '#3b82f6'
                        e.currentTarget.style.color = '#2563eb'
                        e.currentTarget.style.background = '#f8fafc'
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = '#d1d5db'
                        e.currentTarget.style.color = '#6b7280'
                        e.currentTarget.style.background = 'white'
                      }}
                    >
                      <Phone style={{ height: '18px', width: '18px' }} />
                      Add Phone Number
                    </button>
                  )}
                </div>
              )}

              {/* EDITING state — phone input + send code */}
              {phoneStep === 'EDITING' && (
                <div>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                    <div style={{ flex: 1, position: 'relative' }}>
                      <input
                        type="text"
                        value={newPhone}
                        onChange={(e) => { setNewPhone(e.target.value); if (phoneError) setPhoneError('') }}
                        placeholder="+44 7911 123456"
                        style={inputStyle}
                        onFocus={handleInputFocus}
                        onBlur={handleInputBlur}
                        autoFocus
                      />
                      <Phone style={iconStyle} />
                    </div>
                    <button
                      onClick={handleSendVerification}
                      disabled={phoneSending || !newPhone.trim()}
                      style={{
                        padding: '16px 20px',
                        background: (phoneSending || !newPhone.trim())
                          ? '#9ca3af'
                          : 'linear-gradient(to right, #2563eb, #4f46e5)',
                        color: 'white',
                        fontWeight: '600',
                        borderRadius: '12px',
                        border: 'none',
                        cursor: (phoneSending || !newPhone.trim()) ? 'not-allowed' : 'pointer',
                        fontSize: '14px',
                        fontFamily: 'Lexend Deca, sans-serif',
                        whiteSpace: 'nowrap',
                        transition: 'all 0.2s',
                        opacity: (phoneSending || !newPhone.trim()) ? 0.6 : 1
                      }}
                    >
                      {phoneSending ? 'Sending...' : 'Send Code'}
                    </button>
                  </div>
                  <p style={{
                    marginTop: '8px',
                    fontSize: '13px',
                    color: '#9ca3af',
                    fontFamily: 'Lexend Deca, sans-serif'
                  }}>
                    UK numbers only: +44 followed by 10 digits (e.g., +447911123456)
                  </p>
                  <button
                    onClick={cancelPhoneEdit}
                    style={{
                      marginTop: '12px',
                      background: 'transparent',
                      border: 'none',
                      color: '#6b7280',
                      fontSize: '14px',
                      fontFamily: 'Lexend Deca, sans-serif',
                      cursor: 'pointer',
                      padding: '4px 0',
                      display: 'inline-flex',
                      alignItems: 'center',
                      transition: 'color 0.2s'
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.color = '#374151' }}
                    onMouseLeave={(e) => { e.currentTarget.style.color = '#6b7280' }}
                  >
                    <X style={{ height: '14px', width: '14px', marginRight: '4px' }} />
                    Cancel
                  </button>
                </div>
              )}

              {/* CODE_SENT state — OTP input + verify */}
              {phoneStep === 'CODE_SENT' && (
                <div>
                  <p style={{
                    fontSize: '14px',
                    color: '#374151',
                    fontFamily: 'Lexend Deca, sans-serif',
                    marginBottom: '16px'
                  }}>
                    Enter the 6-digit code sent to <strong>{newPhone.trim()}</strong>
                  </p>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                    <input
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      maxLength={6}
                      value={verifyCode}
                      onChange={(e) => {
                        const val = e.target.value.replace(/\D/g, '')
                        setVerifyCode(val)
                        if (phoneError) setPhoneError('')
                      }}
                      placeholder="000000"
                      style={{
                        flex: 1,
                        padding: '16px',
                        border: '1px solid #d1d5db',
                        borderRadius: '12px',
                        fontSize: '24px',
                        fontWeight: '600',
                        background: 'white',
                        color: '#111827',
                        fontFamily: 'Lexend Deca, sans-serif',
                        outline: 'none',
                        transition: 'all 0.2s',
                        textAlign: 'center',
                        letterSpacing: '0.4em',
                        boxSizing: 'border-box' as const
                      }}
                      onFocus={handleInputFocus}
                      onBlur={handleInputBlur}
                      autoFocus
                    />
                    <button
                      onClick={handleVerifyCode}
                      disabled={phoneVerifying || verifyCode.length !== 6}
                      style={{
                        padding: '16px 20px',
                        background: (phoneVerifying || verifyCode.length !== 6)
                          ? '#9ca3af'
                          : 'linear-gradient(to right, #2563eb, #4f46e5)',
                        color: 'white',
                        fontWeight: '600',
                        borderRadius: '12px',
                        border: 'none',
                        cursor: (phoneVerifying || verifyCode.length !== 6) ? 'not-allowed' : 'pointer',
                        fontSize: '14px',
                        fontFamily: 'Lexend Deca, sans-serif',
                        whiteSpace: 'nowrap',
                        transition: 'all 0.2s',
                        opacity: (phoneVerifying || verifyCode.length !== 6) ? 0.6 : 1
                      }}
                    >
                      {phoneVerifying ? 'Verifying...' : 'Verify'}
                    </button>
                  </div>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginTop: '12px'
                  }}>
                    <button
                      onClick={cancelPhoneEdit}
                      style={{
                        background: 'transparent',
                        border: 'none',
                        color: '#6b7280',
                        fontSize: '14px',
                        fontFamily: 'Lexend Deca, sans-serif',
                        cursor: 'pointer',
                        padding: '4px 0',
                        display: 'inline-flex',
                        alignItems: 'center',
                        transition: 'color 0.2s'
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.color = '#374151' }}
                      onMouseLeave={(e) => { e.currentTarget.style.color = '#6b7280' }}
                    >
                      <X style={{ height: '14px', width: '14px', marginRight: '4px' }} />
                      Cancel
                    </button>
                    <button
                      onClick={handleSendVerification}
                      disabled={resendCooldown > 0 || phoneSending}
                      style={{
                        background: 'transparent',
                        border: 'none',
                        color: resendCooldown > 0 ? '#9ca3af' : '#2563eb',
                        fontSize: '14px',
                        fontFamily: 'Lexend Deca, sans-serif',
                        cursor: resendCooldown > 0 ? 'not-allowed' : 'pointer',
                        padding: '4px 0',
                        transition: 'color 0.2s'
                      }}
                    >
                      {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend code'}
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Divider */}
            <div style={{
              borderTop: '1px solid #e5e7eb',
              marginTop: '32px',
              paddingTop: '32px'
            }}>
              {/* Password Section */}
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
                <Lock style={{ height: '20px', width: '20px', color: '#2563eb', marginRight: '8px' }} />
                <h3 style={{
                  fontSize: '16px',
                  fontWeight: '600',
                  color: '#1f2937',
                  fontFamily: 'Lexend Deca, sans-serif'
                }}>
                  {hasPassword ? 'Change Password' : 'Add Password'}
                </h3>
              </div>

              {/* Password Error Message */}
              {passwordError && (
                <div style={{
                  borderRadius: '12px',
                  padding: '12px 16px',
                  background: '#fef2f2',
                  border: '1px solid #fecaca',
                  marginBottom: '16px'
                }}>
                  <p style={{ fontSize: '14px', fontWeight: '500', color: '#dc2626', fontFamily: 'Lexend Deca, sans-serif' }}>
                    {passwordError}
                  </p>
                </div>
              )}

              {hasPassword ? (
                /* ── Change Password flow (has existing password) ── */
                <>
                  <p style={{
                    fontSize: '13px',
                    color: '#6b7280',
                    fontFamily: 'Lexend Deca, sans-serif',
                    marginBottom: '16px'
                  }}>
                    Enter your current password to set a new one. Must be at least 6 characters.
                  </p>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    <div>
                      <label htmlFor="currentPassword" style={labelStyle}>Current Password</label>
                      <div style={{ position: 'relative' }}>
                        <input
                          id="currentPassword"
                          type="password"
                          value={currentPassword}
                          onChange={(e) => { setCurrentPassword(e.target.value); if (passwordError) setPasswordError('') }}
                          placeholder="Enter current password"
                          style={inputStyle}
                          onFocus={handleInputFocus}
                          onBlur={handleInputBlur}
                        />
                        <Lock style={iconStyle} />
                      </div>
                    </div>

                    <div>
                      <label htmlFor="newPassword" style={labelStyle}>New Password</label>
                      <div style={{ position: 'relative' }}>
                        <input
                          id="newPassword"
                          type="password"
                          value={newPassword}
                          onChange={(e) => { setNewPassword(e.target.value); if (passwordError) setPasswordError('') }}
                          placeholder="Enter new password"
                          style={inputStyle}
                          onFocus={handleInputFocus}
                          onBlur={handleInputBlur}
                        />
                        <Lock style={iconStyle} />
                      </div>
                    </div>

                    <div>
                      <label htmlFor="confirmPassword" style={labelStyle}>Confirm New Password</label>
                      <div style={{ position: 'relative' }}>
                        <input
                          id="confirmPassword"
                          type="password"
                          value={confirmPassword}
                          onChange={(e) => { setConfirmPassword(e.target.value); if (passwordError) setPasswordError('') }}
                          placeholder="Confirm new password"
                          style={inputStyle}
                          onFocus={handleInputFocus}
                          onBlur={handleInputBlur}
                        />
                        <Lock style={iconStyle} />
                      </div>
                    </div>

                    <button
                      onClick={handleChangePassword}
                      disabled={changingPassword || !currentPassword || !newPassword || !confirmPassword}
                      style={{
                        width: '100%',
                        padding: '16px 24px',
                        background: (changingPassword || !currentPassword || !newPassword || !confirmPassword)
                          ? '#9ca3af'
                          : 'linear-gradient(to right, #2563eb, #4f46e5)',
                        color: 'white',
                        fontWeight: '600',
                        borderRadius: '12px',
                        boxShadow: (changingPassword || !currentPassword || !newPassword || !confirmPassword)
                          ? 'none'
                          : '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
                        border: 'none',
                        cursor: (changingPassword || !currentPassword || !newPassword || !confirmPassword) ? 'not-allowed' : 'pointer',
                        fontSize: '16px',
                        fontFamily: 'Lexend Deca, sans-serif',
                        transition: 'all 0.2s',
                        opacity: (changingPassword || !currentPassword || !newPassword || !confirmPassword) ? 0.6 : 1
                      }}
                      onMouseEnter={(e) => {
                        if (!changingPassword && currentPassword && newPassword && confirmPassword) {
                          const target = e.currentTarget as HTMLButtonElement
                          target.style.background = 'linear-gradient(to right, #1d4ed8, #4338ca)'
                          target.style.boxShadow = '0 20px 25px -5px rgba(0, 0, 0, 0.1)'
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!changingPassword && currentPassword && newPassword && confirmPassword) {
                          const target = e.currentTarget as HTMLButtonElement
                          target.style.background = 'linear-gradient(to right, #2563eb, #4f46e5)'
                          target.style.boxShadow = '0 10px 15px -3px rgba(0, 0, 0, 0.1)'
                        }
                      }}
                    >
                      {changingPassword ? (
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <div style={{
                            animation: 'spin 1s linear infinite',
                            borderRadius: '50%',
                            height: '20px',
                            width: '20px',
                            border: '2px solid white',
                            borderBottomColor: 'transparent',
                            marginRight: '8px'
                          }}></div>
                          Updating...
                        </div>
                      ) : (
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <Lock style={{ height: '20px', width: '20px', marginRight: '8px' }} />
                          Update Password
                        </div>
                      )}
                    </button>
                  </div>
                </>
              ) : (
                /* ── Set Password flow (no password yet — email confirmation) ── */
                <>
                  {setupEmailSent ? (
                    <div style={{
                      borderRadius: '12px',
                      padding: '16px',
                      background: '#eff6ff',
                      border: '1px solid #bfdbfe'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                        <Mail style={{ height: '18px', width: '18px', color: '#2563eb', marginRight: '10px', flexShrink: 0 }} />
                        <p style={{ fontSize: '14px', fontWeight: '600', color: '#1e40af', fontFamily: 'Lexend Deca, sans-serif' }}>
                          Check your email
                        </p>
                      </div>
                      <p style={{
                        fontSize: '13px',
                        color: '#3b82f6',
                        fontFamily: 'Lexend Deca, sans-serif',
                        lineHeight: '1.5'
                      }}>
                        We've sent a password setup link to <strong>{user?.email}</strong>. Click the link in the email to set your password.
                      </p>
                    </div>
                  ) : (
                    <>
                      <p style={{
                        fontSize: '13px',
                        color: '#6b7280',
                        fontFamily: 'Lexend Deca, sans-serif',
                        marginBottom: '16px',
                        lineHeight: '1.5'
                      }}>
                        Add a password to enable password-based sign-in. We'll send a confirmation link to your email address to verify your identity first.
                      </p>
                      <button
                        onClick={handleSendSetupEmail}
                        disabled={sendingSetupEmail}
                        style={{
                          width: '100%',
                          padding: '16px 24px',
                          background: sendingSetupEmail
                            ? '#9ca3af'
                            : 'linear-gradient(to right, #2563eb, #4f46e5)',
                          color: 'white',
                          fontWeight: '600',
                          borderRadius: '12px',
                          boxShadow: sendingSetupEmail
                            ? 'none'
                            : '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
                          border: 'none',
                          cursor: sendingSetupEmail ? 'not-allowed' : 'pointer',
                          fontSize: '16px',
                          fontFamily: 'Lexend Deca, sans-serif',
                          transition: 'all 0.2s',
                          opacity: sendingSetupEmail ? 0.6 : 1
                        }}
                        onMouseEnter={(e) => {
                          if (!sendingSetupEmail) {
                            const target = e.currentTarget as HTMLButtonElement
                            target.style.background = 'linear-gradient(to right, #1d4ed8, #4338ca)'
                            target.style.boxShadow = '0 20px 25px -5px rgba(0, 0, 0, 0.1)'
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!sendingSetupEmail) {
                            const target = e.currentTarget as HTMLButtonElement
                            target.style.background = 'linear-gradient(to right, #2563eb, #4f46e5)'
                            target.style.boxShadow = '0 10px 15px -3px rgba(0, 0, 0, 0.1)'
                          }
                        }}
                      >
                        {sendingSetupEmail ? (
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <div style={{
                              animation: 'spin 1s linear infinite',
                              borderRadius: '50%',
                              height: '20px',
                              width: '20px',
                              border: '2px solid white',
                              borderBottomColor: 'transparent',
                              marginRight: '8px'
                            }}></div>
                            Sending...
                          </div>
                        ) : (
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Mail style={{ height: '20px', width: '20px', marginRight: '8px' }} />
                            Send Password Setup Email
                          </div>
                        )}
                      </button>
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
